import React from "react";
import { Box, Button, Typography } from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import BookmarkRemoveOutlinedIcon from "@mui/icons-material/BookmarkRemoveOutlined";
import BookmarkAddOutlinedIcon from "@mui/icons-material/BookmarkAddOutlined";
import { API, Auth, graphqlOperation } from "aws-amplify";
import { deleteQuestion, createQuestion } from "../graphql/mutations";
import AudioRecorder from "./AudioRecorder";

const INK = "#0f1121";
const INK_SOFT = "rgba(15, 17, 33, 0.62)";
const INK_FAINT = "rgba(15, 17, 33, 0.42)";
const INK_HAIRLINE = "rgba(15, 17, 33, 0.08)";

const SANS = "'sohne-var', 'Helvetica Neue', 'Arial', sans-serif";

const ghostBtnSx = {
  fontFamily: SANS,
  fontSize: "0.82rem",
  fontWeight: 500,
  color: INK_SOFT,
  textTransform: "none",
  px: 1.25,
  borderRadius: "10px",
  transition: "color 0.15s ease, background-color 0.15s ease",
  "&:hover": {
    color: INK,
    backgroundColor: "rgba(15, 17, 33, 0.04)"
  }
};

const SavedQuestion = ({
  question,
  questionID,
  removeQuestion,
  conversation,
  blobURL,
  audioKey,
  responseText,
  feedback,
  feedbackGeneratedAt,
  onBlobURLChange,
  onAudioKeyChange,
  onResponseTextChange,
  onConversationTextChange,
  onFeedbackChange,
  unsaved
}) => {
  const isUnsaved = unsaved === 1;

  const saveQuestion = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      if (!user || user.username === undefined) return;
      const newQuestion = {
        questionText: question,
        feedback,
        responseText,
        qID: user.username,
        conversation
      };
      await API.graphql(
        graphqlOperation(createQuestion, { input: newQuestion })
      );
    } catch (error) {
      console.error("Error creating question:", error);
    }
  };

  const deleteQuestionById = async () => {
    try {
      const input = { id: questionID };
      await API.graphql(graphqlOperation(deleteQuestion, { input }));
      if (typeof removeQuestion === "function") removeQuestion();
    } catch (error) {
      console.error("Error deleting question:", error);
    }
  };

  const handleRetry = () => {
    onBlobURLChange("");
    onAudioKeyChange("");
    onResponseTextChange("");
    onFeedbackChange("");
  };

  const hasContent = blobURL || audioKey || responseText || feedback;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 3
        }}
      >
        <Typography
          sx={{
            fontFamily: SANS,
            fontSize: { xs: "1.15rem", md: "1.3rem" },
            fontWeight: 500,
            lineHeight: 1.3,
            letterSpacing: "-0.01em",
            color: INK,
            flex: 1,
            minWidth: 0
          }}
        >
          {question}
        </Typography>
        <Button
          onClick={isUnsaved ? saveQuestion : deleteQuestionById}
          startIcon={
            isUnsaved ? (
              <BookmarkAddOutlinedIcon sx={{ fontSize: 16 }} />
            ) : (
              <BookmarkRemoveOutlinedIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={ghostBtnSx}
        >
          {isUnsaved ? "Save" : "Unsave"}
        </Button>
      </Box>

      <AudioRecorder
        question={question}
        conversation={conversation}
        onConversationTextChange={onConversationTextChange}
        blobURL={blobURL}
        audioKey={audioKey}
        responseText={responseText}
        feedback={feedback}
        feedbackGeneratedAt={feedbackGeneratedAt}
        onBlobURLChange={onBlobURLChange}
        onAudioKeyChange={onAudioKeyChange}
        onResponseTextChange={onResponseTextChange}
        onFeedbackChange={onFeedbackChange}
      />

      {hasContent && (
        <Box
          sx={{
            mt: 3,
            pt: 2.5,
            borderTop: `1px solid ${INK_HAIRLINE}`,
            display: "flex",
            justifyContent: "flex-start"
          }}
        >
          <Button
            onClick={handleRetry}
            startIcon={<ReplayIcon sx={{ fontSize: 16 }} />}
            sx={{
              ...ghostBtnSx,
              color: INK_FAINT,
              "&:hover": { color: INK, backgroundColor: "rgba(15, 17, 33, 0.04)" }
            }}
          >
            Retry this question
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SavedQuestion;
